// components/screener/useScreener.js
import { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../../config/axiosConfig';
import { getFilterById } from './screenerConfig';

const DEFAULT_PAGE_SIZE = 25;

// ── URL ↔ Filter helpers ────────────────────────────────────────────────────

const filtersToUrlParams = (filterData) => {
    const params = new URLSearchParams();
    Object.entries(filterData).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value)) {
            if (value.length > 0) params.set(key, value.map(v => encodeURIComponent(v)).join(','));
        } else {
            params.set(key, encodeURIComponent(value.toString()));
        }
    });
    return params.toString();
};

const parseUrlParams = (search) => {
    const filters = {};
    const params = new URLSearchParams(search);
    for (const [key, value] of params.entries()) {
        if (value === '') continue;
        if (key === 'exchange' || key === 'consensus') {
            filters[key] = value.split(',').map(v => decodeURIComponent(v.trim()));
        } else if (key === 'isEtf') {
            filters[key] = value === 'true';
        } else if (key.includes('MoreThan') || key.includes('LowerThan') ||
            key === 'minPercentage' || key === 'maxPercentage') {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) filters[key] = numValue;
        } else {
            filters[key] = decodeURIComponent(value);
        }
    }
    return filters;
};

const rawFiltersToActiveFilters = (filterData) => {
    const activeFilters = [];

    Object.entries(filterData).forEach(([key, value]) => {
        // Handle moving average composite
        if (['averageType', 'comparisonType', 'minPercentage', 'maxPercentage'].includes(key)) {
            if (!activeFilters.find(f => f.id === 'averageComparison')) {
                const def = getFilterById('averageComparison');
                if (def) {
                    activeFilters.push({
                        ...def,
                        values: {
                            averageType: filterData.averageType || '50',
                            comparisonType: filterData.comparisonType || 'range',
                            minPercentage: filterData.minPercentage || '0',
                            maxPercentage: filterData.maxPercentage || '5',
                        },
                    });
                }
            }
            return;
        }

        // Handle range keys
        if (key.endsWith('MoreThan') || key.endsWith('LowerThan')) {
            const baseId = key.replace(/MoreThan$/, '').replace(/LowerThan$/, '');
            const existing = activeFilters.find(f => f.id === baseId);
            const def = getFilterById(baseId);
            if (def) {
                if (existing) {
                    if (key.endsWith('MoreThan')) existing.values.min = value;
                    if (key.endsWith('LowerThan')) existing.values.max = value;
                } else {
                    activeFilters.push({
                        ...def,
                        values: {
                            min: key.endsWith('MoreThan') ? value : '',
                            max: key.endsWith('LowerThan') ? value : '',
                            mode: 'custom',
                        },
                    });
                }
            }
            return;
        }

        // Handle regular filters
        const def = getFilterById(key);
        if (def) {
            activeFilters.push({ ...def, values: value });
        }
    });

    return activeFilters;
};

const useScreener = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [queryId, setQueryId] = useState(null);
    const [totalResults, setTotalResults] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [totalPages, setTotalPages] = useState(0);
    const [currentView, setCurrentView] = useState('overview');
    const [activeFilters, setActiveFilters] = useState([]);
    const [selectedScreenName, setSelectedScreenName] = useState(null);

    // Keep a ref to current filters for use in callbacks
    const filtersRef = useRef(activeFilters);
    filtersRef.current = activeFilters;
    const lastHydratedViewRef = useRef(null);
    const pageCache = useRef(new Map());
    const pageCacheKey = (view, pg) => `${view}:${pg}`;


    // ── Prepare filterData from active filters ──────────────────────────────
    const prepareFilterData = useCallback((filters = null) => {
        const filtersToUse = filters || filtersRef.current;
        const data = {};

        filtersToUse.forEach(filter => {
            if (filter.type === 'range') {
                const scale = filter.filterScale ?? 1;
                if (filter.values?.min !== '' && filter.values?.min !== undefined) {
                    data[`${filter.id}MoreThan`] = Number(filter.values.min) * scale;
                }
                if (filter.values?.max !== '' && filter.values?.max !== undefined) {
                    data[`${filter.id}LowerThan`] = Number(filter.values.max) * scale;
                }
            } else if (filter.type === 'composite' && filter.id === 'averageComparison') {
                if (filter.values) {
                    data.averageType = filter.values.averageType;
                    data.comparisonType = filter.values.comparisonType;
                    data.minPercentage = filter.values.minPercentage;
                    if (filter.values.comparisonType === 'range') {
                        data.maxPercentage = filter.values.maxPercentage;
                    }
                }
            } else if (filter.type === 'boolean') {
                if (filter.values !== undefined) {
                    data[filter.id] = filter.values;
                }
            } else if (filter.type === 'multiSelect') {
                if (Array.isArray(filter.values) && filter.values.length > 0) {
                    data[filter.id] = filter.values;
                }
            } else if (filter.type === 'select') {
                if (filter.values) {
                    data[filter.id] = filter.values;
                }
            }
        });

        return data;
    }, []);

    // ── Apply response data to state ────────────────────────────────────────
    const applyResponse = useCallback((response, view = null, pg = null) => {
        setResults(response.data || []);
        setQueryId(response.queryId);
        setTotalResults(response.totalResults || 0);
        setPage(response.page || 1);
        setTotalPages(response.totalPages || 0);
        setPageSize(response.pageSize || DEFAULT_PAGE_SIZE);
        if (view) {
            lastHydratedViewRef.current = view;
            const cacheKey = pageCacheKey(view, response.page || pg || 1);
            pageCache.current.set(cacheKey, response);
        }
    }, []);

    // ── SEARCH: Run full filter pipeline ────────────────────────────────────
    const search = useCallback(async (filters = null, view = null) => {
        setIsLoading(true);
        setError(null);
        setResults([]);
        pageCache.current.clear();

        try {
            const filterData = filters
                ? (typeof filters === 'object' && !Array.isArray(filters) && !filters.id
                    ? filters  // Raw filterData object (from predefined screens)
                    : prepareFilterData(filters))
                : prepareFilterData();

            const targetView = view || currentView;

            const response = await axios.post('/api/screener/search', {
                filters: filterData,
                page: 1,
                pageSize,
                view: targetView,
            });

            if (response.data.error === 'PLAN_RESTRICTED') {
                setError(response.data.message);
                setIsLoading(false);
                return;
            }

            applyResponse(response.data, targetView, 1);
            setCurrentView(targetView);

            // Sync filters to URL
            const urlParams = filtersToUrlParams(filterData);
            const newUrl = urlParams ? `/screener?${urlParams}` : '/screener';
            navigate(newUrl, { replace: true });
        } catch (err) {
            console.error('Search error:', err);
            setError('Failed to fetch results. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [prepareFilterData, currentView, pageSize, applyResponse, navigate]);

    // ── PAGE: Get a different page from cached results ──────────────────────
    const goToPage = useCallback(async (newPage) => {
        if (!queryId) return;

        // Check client-side cache first
        const cacheKey = pageCacheKey(currentView, newPage);
        const cached = pageCache.current.get(cacheKey);
        if (cached) {
            applyResponse(cached, currentView);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await axios.get('/api/screener/page', {
                params: { queryId, page: newPage, pageSize, view: currentView },
            });

            if (response.data.error === 'CACHE_EXPIRED') {
                const filterData = prepareFilterData();
                const freshResponse = await axios.post('/api/screener/search', {
                    filters: filterData, page: newPage, pageSize, view: currentView,
                });
                applyResponse(freshResponse.data, currentView);
                return;
            }

            applyResponse(response.data, currentView);
        } catch (err) {
            if (err.response?.status === 410) {
                try {
                    const filterData = prepareFilterData();
                    const freshResponse = await axios.post('/api/screener/search', {
                        filters: filterData, page: newPage, pageSize, view: currentView,
                    });
                    applyResponse(freshResponse.data, currentView);
                } catch (retryErr) {
                    console.error('Page re-search error:', retryErr);
                    setError('Failed to load page.');
                }
                return;
            }
            console.error('Page error:', err);
            setError('Failed to load page.');
        } finally {
            setIsLoading(false);
        }
    }, [queryId, pageSize, currentView, prepareFilterData, applyResponse]);

    // ── VIEW: Switch tab/view (re-hydrates same page) ───────────────────────
    const switchView = useCallback(async (newView) => {
        setCurrentView(newView);

        if (!queryId) return;
        if (lastHydratedViewRef.current === 'all') return;

        // Check client-side cache first
        const cacheKey = pageCacheKey(newView, page);
        const cached = pageCache.current.get(cacheKey);
        if (cached) {
            applyResponse(cached, newView);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await axios.get('/api/screener/page', {
                params: { queryId, page, pageSize, view: newView },
            });

            if (response.data.error === 'CACHE_EXPIRED') {
                const filterData = prepareFilterData();
                const freshResponse = await axios.post('/api/screener/search', {
                    filters: filterData, page, pageSize, view: newView,
                });
                applyResponse(freshResponse.data, newView);
                return;
            }

            applyResponse(response.data, newView);
        } catch (err) {
            if (err.response?.status === 410) {
                try {
                    const filterData = prepareFilterData();
                    const freshResponse = await axios.post('/api/screener/search', {
                        filters: filterData, page, pageSize, view: newView,
                    });
                    applyResponse(freshResponse.data, newView);
                } catch (retryErr) {
                    console.error('View switch re-search error:', retryErr);
                    setError('Failed to load view. Please try again.');
                }
                return;
            }
            console.error('View switch error:', err);
        } finally {
            setIsLoading(false);
        }
    }, [queryId, page, pageSize, prepareFilterData, applyResponse]);

    // ── CHANGE PAGE SIZE ────────────────────────────────────────────────────
    const changePageSize = useCallback(async (newPageSize) => {
        setPageSize(newPageSize);
        if (queryId) {
            setIsLoading(true);
            try {
                const response = await axios.get('/api/screener/page', {
                    params: { queryId, page: 1, pageSize: newPageSize, view: currentView },
                });
                if (response.data.error === 'CACHE_EXPIRED') {
                    await search();
                    return;
                }
                applyResponse(response.data, currentView);
            } catch (err) {
                console.error('Page size change error:', err);
            } finally {
                setIsLoading(false);
            }
        }
    }, [queryId, currentView, search, applyResponse]);

    // ── FILTER MANAGEMENT ───────────────────────────────────────────────────

    const addFilter = useCallback((filterConfig) => {
        setActiveFilters(prev => {
            if (prev.find(f => f.id === filterConfig.id)) return prev;

            let initialValues;
            if (filterConfig.type === 'range') {
                initialValues = { min: '', max: '', mode: 'custom' };
            } else if (filterConfig.type === 'composite' && filterConfig.id === 'averageComparison') {
                initialValues = {
                    averageType: '50',
                    comparisonType: 'range',
                    minPercentage: '0',
                    maxPercentage: '5',
                };
            } else if (filterConfig.type === 'boolean') {
                initialValues = undefined; // Not set yet
            } else if (filterConfig.type === 'multiSelect') {
                initialValues = [];
            } else if (filterConfig.type === 'select') {
                initialValues = filterConfig.defaultValue || '';
            } else {
                initialValues = '';
            }

            return [...prev, { ...filterConfig, values: initialValues }];
        });
    }, []);

    const removeFilter = useCallback((filterId) => {
        setActiveFilters(prev => prev.filter(f => f.id !== filterId));
    }, []);

    const updateFilterValues = useCallback((filterId, newValues) => {
        setActiveFilters(prev =>
            prev.map(f => f.id === filterId ? { ...f, values: newValues } : f)
        );
    }, []);

    // ── PREDEFINED SCREENS ──────────────────────────────────────────────────
    const applyPredefinedScreen = useCallback(async (screen) => {
        setSelectedScreenName(screen.name);

        // Convert predefined screen filters to active filter objects
        const newActiveFilters = [];
        const filterData = screen.filters;

        Object.entries(filterData).forEach(([key, value]) => {
            // Handle MoreThan/LowerThan range keys
            if (key.endsWith('MoreThan') || key.endsWith('LowerThan')) {
                const baseId = key.replace(/MoreThan$/, '').replace(/LowerThan$/, '');
                const existing = newActiveFilters.find(f => f.id === baseId);
                const filterDef = getFilterById(baseId);

                if (filterDef) {
                    if (existing) {
                        if (key.endsWith('MoreThan')) existing.values.min = value;
                        if (key.endsWith('LowerThan')) existing.values.max = value;
                    } else {
                        newActiveFilters.push({
                            ...filterDef,
                            values: {
                                min: key.endsWith('MoreThan') ? value : '',
                                max: key.endsWith('LowerThan') ? value : '',
                                mode: 'custom',
                            },
                        });
                    }
                }
                return;
            }

            // Handle composite (moving average)
            if (['averageType', 'comparisonType', 'minPercentage', 'maxPercentage'].includes(key)) {
                let existing = newActiveFilters.find(f => f.id === 'averageComparison');
                const filterDef = getFilterById('averageComparison');

                if (!existing && filterDef) {
                    existing = {
                        ...filterDef,
                        values: {
                            averageType: filterData.averageType || '50',
                            comparisonType: filterData.comparisonType || 'range',
                            minPercentage: filterData.minPercentage || '0',
                            maxPercentage: filterData.maxPercentage || '5',
                        },
                    };
                    newActiveFilters.push(existing);
                } else if (existing) {
                    existing.values[key] = value;
                }
                return;
            }

            // Handle regular filters
            const filterDef = getFilterById(key);
            if (filterDef) {
                newActiveFilters.push({
                    ...filterDef,
                    values: value,
                });
            }
        });

        setActiveFilters(newActiveFilters);

        // Trigger search with raw filterData
        await search(filterData);
    }, [search]);

    // ── CLEAR ───────────────────────────────────────────────────────────────
    const clear = useCallback(() => {
        setActiveFilters([]);
        setResults([]);
        setQueryId(null);
        setTotalResults(0);
        setPage(1);
        setTotalPages(0);
        setSelectedScreenName(null);
        setError(null);
        lastHydratedViewRef.current = null;
        pageCache.current.clear();
        navigate('/screener', { replace: true });
    }, [navigate]);

    // ── URL INIT: Parse URL params on mount and auto-search ─────────────────
    const hasInitRef = useRef(false);
    useEffect(() => {
        if (hasInitRef.current) return;

        const urlFilters = parseUrlParams(location.search);
        if (Object.keys(urlFilters).length > 0) {
            hasInitRef.current = true;
            const parsedActiveFilters = rawFiltersToActiveFilters(urlFilters);
            setActiveFilters(parsedActiveFilters);

            setTimeout(() => {
                search(urlFilters);
            }, 100);
        } else {
            hasInitRef.current = true;
        }
    }, [location.search, search]);

    return {
        // State
        results,
        isLoading,
        error,
        queryId,
        totalResults,
        page,
        pageSize,
        totalPages,
        currentView,
        activeFilters,
        selectedScreenName,

        // Actions
        search,
        goToPage,
        switchView,
        changePageSize,
        addFilter,
        removeFilter,
        updateFilterValues,
        applyPredefinedScreen,
        clear,
        prepareFilterData,
    };
};

export default useScreener;